import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  useTheme,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Collapse,
  Paper
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { AddPhotoAlternate, QrCode2, AddCircleOutline, ArrowBack } from '@mui/icons-material';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useAuth } from '../context/AuthProvider.tsx';
import uploadToS3 from '../api/uploadToS3.ts';
import { ThemeType, themeConfigs } from './ThemeConfigs.ts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import QRScanner from './QRScanner.tsx';
import { useNavigate } from 'react-router-dom';

interface WizardModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
}

interface FormData {
  title: string;
  description: string;
  message: string;
  unlockDate: Date | null;
  media: File[];
  images: { image: string; comments: any[]; owner?: string; timestamp?: string }[];
  isShared: boolean;
  access: string[];
  theme: ThemeType;
  tempFiles: File[]; // Store files temporarily before upload
}

interface Friend {
  username: string;
  name: string;
  picture: string;
}

interface FormErrors {
  title: string;
  description: string;
  unlockDate: string;
}

const initialFormData: FormData = {
  title: '',
  description: '',
  message: '',
  unlockDate: new Date(new Date().setDate(new Date().getDate() + 1)),
  media: [],
  images: [],
  isShared: false,
  access: [],
  theme: 'classic',
  tempFiles: []
};

const initialFormErrors: FormErrors = {
  title: '',
  description: '',
  unlockDate: '',
};

const WizardModal: React.FC<WizardModalProps> = ({ open, onClose, onSubmit }) => {
  const [showLandingDialog, setShowLandingDialog] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
  const [friends, setFriends] = useState<Friend[]>([]);
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemeType | null>('classic');
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const placeholderRef = useRef<NodeJS.Timeout | null>(null);
  const [isUploadSectionOpen, setIsUploadSectionOpen] = useState(false);
  const [isMessageSectionOpen, setIsMessageSectionOpen] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isTypingForward, setIsTypingForward] = useState(true);
  const [currentPosition, setCurrentPosition] = useState(0);

  const navigate = useNavigate();

  const placeholders = [
    "My 2025 Goals",
    "Wedding Memories",
    "Graduation Celebration",
    "Family Vacation 2025",
    "New Year's Resolutions",
    "Time Capsule for My Future Self",
    "Our First Home",
    "Baby's First Year",
    "College Memories",
    "Dream Travel Destinations"
  ];

  // Faster typing animation
  useEffect(() => {
    if (formData.title !== '') {
      setTypedPlaceholder('');
      return;
    }

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);

    const currentPlaceholder = placeholders[placeholderIndex];
    
    const animateTyping = () => {
      if (isTypingForward) {
        if (currentPosition < currentPlaceholder.length) {
          setTypedPlaceholder(currentPlaceholder.substring(0, currentPosition + 1));
          setCurrentPosition(prev => prev + 1);
          // Significantly faster typing (20ms instead of 100ms)
          typingTimerRef.current = setTimeout(animateTyping, 10);
        } else {
          typingTimerRef.current = setTimeout(() => {
            setIsTypingForward(false);
            animateTyping();
          }, 1);
        }
      } else {
        if (currentPosition > 0) {
          setCurrentPosition(prev => prev - 1);
          setTypedPlaceholder(currentPlaceholder.substring(0, currentPosition - 1));
          // Faster erasing (15ms instead of 50ms)
          typingTimerRef.current = setTimeout(animateTyping, 10);
        } else {
          const nextIndex = (placeholderIndex + 1) % placeholders.length;
          setPlaceholderIndex(nextIndex);
          setIsTypingForward(true);
          typingTimerRef.current = setTimeout(animateTyping, 10);
        }
      }
    };

    // Start animation faster
    typingTimerRef.current = setTimeout(animateTyping, 100);

    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [formData.title, placeholderIndex, placeholders, isTypingForward, currentPosition]);

  useEffect(() => {
    const fetchFriendsList = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const authToken = tokens?.accessToken?.toString();

        const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friends/accepted/${user?.username}?status=accepted`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': authToken!,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch friends list');
        }

        const result = await response.json();
        setFriends(result);
      } catch (error) {
        console.error('Error fetching friends list:', error);
      }
    };

    fetchFriendsList();
  }, [user]);

  const handleStartQRScan = () => {
    setShowLandingDialog(false);
    onClose();
    setShowQRScanner(true);
  };

  const handleQRScanComplete = (data: string) => {   
    const modifiedData = new URL(data).search;
    navigate(modifiedData, { state: { refreshCapsules: true } });
  };

  const handleQRScanCancel = () => {
    setShowQRScanner(false);
    setShowLandingDialog(true);
  };

  const handleCreateNewCapsule = () => setShowLandingDialog(false);
  const handleBackToLanding = () => {
    setActiveStep(0);
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    setShowLandingDialog(true);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors: FormErrors = { ...initialFormErrors };

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
      isValid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }

    if (activeStep === 1 && !formData.unlockDate) {
      newErrors.unlockDate = 'Unlock date is required';
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateForm()) return;
    if (activeStep === 1 && !formData.unlockDate) {
      setFormErrors(prev => ({ ...prev, unlockDate: 'Unlock date is required' }));
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleThemeSelect = (theme: ThemeType) => {
    if (theme === 'classic' || user?.subscriptionStatus === 'premium') {
      setSelectedTheme(theme);
      setFormData(prev => ({ ...prev, theme }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setIsUploading(true);
      setUploadProgress(0);
      
      // Store files locally instead of uploading to S3 immediately
      setFormData(prev => ({
        ...prev,
        tempFiles: [...prev.tempFiles, ...files]
      }));

      // Simulate progress for better UX
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 5;
        if (progress >= 100) {
          clearInterval(progressInterval);
          setIsUploading(false);
        }
        setUploadProgress(progress);
      }, 50);

      // Clear the interval if it completes before 100%
      setTimeout(() => {
        clearInterval(progressInterval);
        setIsUploading(false);
        setUploadProgress(100);
      }, 1000);
    }
  };

  const handleDateChange = (date: Date | null) => {
    setFormData(prev => ({ ...prev, unlockDate: date }));
    setFormErrors(prev => ({ ...prev, unlockDate: '' }));
  };

  const handleSharingToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, isShared: event.target.checked, access: [] }));
  };

  const handleAccessChange = (event: React.SyntheticEvent, value: Friend[]) => {
    const usernames = value.map(friend => friend.username);
    setFormData(prev => ({ ...prev, access: usernames }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    const { tokens } = await fetchAuthSession();
    const authToken = tokens?.accessToken?.toString();
    const currentUser = user?.username;
    const createdTimestamp = new Date().toISOString();
    
    try {
      // Create a copy of the formData to modify
      const submissionData = {...formData};
      
      // Close the modal immediately for better UX
      handleCancel();
      
      // Set up background uploading
      if (formData.tempFiles.length > 0) {
        setIsUploading(true);
        setUploadProgress(0);
        
        // Process files in the background
        (async () => {
          for (let i = 0; i < formData.tempFiles.length; i++) {
            try {
              const file = formData.tempFiles[i];
              
              // Get image timestamp if available from EXIF data
              let imageTimestamp = createdTimestamp;
              try {
                // This is a simplification - in a real implementation, 
                // you would extract the actual timestamp from the image metadata
                // using a library like exif-js
                if (file.type.startsWith('image/')) {
                  // For now, use current date as a fallback
                  imageTimestamp = createdTimestamp;
                }
              } catch (err) {
                console.error("Error extracting image metadata:", err);
              }
              
              // Upload file to S3
              const imageUrl = await uploadToS3(file);
              const filename = imageUrl?.split('/').pop();
              
              if (filename) {
                // Add owner and timestamp to each image
                submissionData.images.push({ 
                  image: filename, 
                  comments: [],
                  owner: currentUser,
                  timestamp: imageTimestamp
                });
              }
              
              // Update progress
              setUploadProgress(((i + 1) / formData.tempFiles.length) * 100);
            } catch (error) {
              console.error("Error uploading file:", error);
            }
          }
          
          // Reset uploading state
          setIsUploading(false);
          
          // Call the onSubmit callback with the updated data
          onSubmit(submissionData);
          
          // Remove tempFiles from the data sent to the API as they're not needed
          const { tempFiles, ...dataToSubmit } = submissionData;
          
          const response = await fetch('https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsule/create', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': authToken!
            },
            body: JSON.stringify({
              ...dataToSubmit,
              access: [...dataToSubmit.access, currentUser],
              owner: currentUser,
              status: "active",
              created: createdTimestamp
            }),
          });

          if (!response.ok) throw new Error('Network response error');
          
          // Navigate to homepage with loading indicator instead of hard reload
          navigate('/', { state: { refreshCapsules: true } });
        })();
      } else {
        // No files to upload, just submit the form
        onSubmit(submissionData);
        
        // Remove tempFiles from the data sent to the API as they're not needed
        const { tempFiles, ...dataToSubmit } = submissionData;
        
        const response = await fetch('https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsule/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': authToken!
          },
          body: JSON.stringify({
            ...dataToSubmit,
            access: [...dataToSubmit.access, currentUser],
            owner: currentUser,
            status: "active",
            created: createdTimestamp
          }),
        });

        if (!response.ok) throw new Error('Network response error');
        
        // Navigate to homepage with loading indicator instead of hard reload
        navigate('/', { state: { refreshCapsules: true } });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    setActiveStep(0);
    setShowLandingDialog(true);
    onClose();
  };

  const handleClose = () => {
    setShowLandingDialog(true);
    onClose();
  };

  const steps = isMobile ? ['Details', 'Settings', 'Theme'] : ['Basic Information & Media', 'Date & Sharing', 'Select Theme'];

  const renderThemeCards = () => (
    <Grid container spacing={2} sx={{ mb: 1 }}>
      {Object.entries(themeConfigs).map(([themeName, themeConfig]) => {
        const isSelected = selectedTheme === themeName;

        return (
          <Grid item xs={6} sm={4} key={themeName}>
            <Card sx={{
              backgroundColor: `${themeConfig.accentColor}30`,
              opacity: themeName === 'classic' || user?.subscriptionStatus === 'premium' ? 1 : 0.5,
              cursor: themeName === 'classic' || user?.subscriptionStatus === 'premium' ? 'pointer' : 'not-allowed',
              position: 'relative',
            }}>
              <CardActionArea
                onClick={() => handleThemeSelect(themeName as ThemeType)}
                disabled={!(themeName === 'classic' || user?.subscriptionStatus === 'premium')}
              >
                <CardContent>
                  <Typography variant="h6" style={{ color: themeConfig.titleColor }}>
                    {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
                  </Typography>
                  <Typography variant="body2" style={{ color: themeConfig.textColor }}>
                    Sample text
                  </Typography>
                  <Box sx={{
                    width: '100%',
                    height: '20px',
                    backgroundColor: themeConfig.accentColor,
                    marginTop: '8px',
                  }} />
                </CardContent>
                {isSelected && (
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 4px rgba(0, 0, 0, 0.2), 0 0 4px rgba(0, 0, 0, 0.2)',
                    zIndex: 1,
                  }}>
                    <CheckCircleIcon sx={{ color: themeConfig.accentColor, fontSize: '20px' }} />
                  </Box>
                )}
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderLandingDialog = () => (
    <>
      <DialogTitle sx={{ p: 0, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 700,
            background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0,
          }}>
            Time Capsule
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Choose an option to get started
          </Typography>
        </Box>
        <IconButton onClick={handleCancel} size="small" sx={{
          top: isMobile ? -16 : -22,
          color: 'text.secondary',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mb: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              border: '1px solid rgba(123, 44, 191, 0.2)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: !isMobile ? '0 10px 20px rgba(123, 44, 191, 0.1)' : 'none',
                borderColor: 'rgba(123, 44, 191, 0.4)',
              },
            }} onClick={handleCreateNewCapsule}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(123, 44, 191, 0.1), rgba(227, 27, 136, 0.1))',
                  mb: 2,
                }}>
                  <AddCircleOutline sx={{ fontSize: 40, color: '#7B2CBF' }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>Create New Capsule</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a new time capsule to store your memories and unlock them later
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              border: '1px solid rgba(123, 44, 191, 0.2)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: !isMobile ? '0 10px 20px rgba(123, 44, 191, 0.1)' : 'none',
                borderColor: 'rgba(123, 44, 191, 0.4)',
              },
            }} onClick={handleStartQRScan}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(123, 44, 191, 0.1), rgba(227, 27, 136, 0.1))',
                  mb: 2,
                }}>
                  <QrCode2 sx={{ fontSize: 40, color: '#E31B88' }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>Scan QR Code</Typography>
                <Typography variant="body2" color="text.secondary">
                  Scan a QR code to join a friend's time capsule
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  );

  const renderStepZero = () => (
    <Box>
      <Box sx={{ position: 'relative', mb: 2 }}>
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          variant="outlined"
          error={!!formErrors.title}
          helperText={formErrors.title}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#7B2CBF',
                borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
              },
            },
            '& .MuiInputLabel-outlined': {
              transform: 'translate(14px, -18px) scale(0.75)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -12px) scale(0.75)',
              },
            },
            '& .MuiInputLabel-outlined.Mui-focused': {
              color: '#7B2CBF',
            },
          }}
        />
        <AnimatePresence>
          {formData.title === '' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                left: '14px',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="body2" sx={{
                background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
              }}>
                {typedPlaceholder}
                <motion.span 
                  animate={{ opacity: [0, 1, 0] }} 
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  style={{ marginLeft: 1, fontWeight: 'bold' }}
                >
                  |
                </motion.span>
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      <TextField
        fullWidth
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        multiline
        rows={3}
        required
        variant="outlined"
        error={!!formErrors.description}
        helperText={formErrors.description}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: '#7B2CBF',
              borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#7B2CBF' }
        }}
      />

      <Button onClick={() => setIsMessageSectionOpen(!isMessageSectionOpen)} sx={{
        width: '100%',
        justifyContent: 'space-between',
        textTransform: 'none',
        mb: 1,
        color: 'text.primary',
        '&:hover': { backgroundColor: 'rgba(123, 44, 191, 0.05)' }
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Add Secret Message</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Optional</Typography>
      </Button>
      <Collapse in={isMessageSectionOpen}>
        <TextField
          fullWidth
          label="Message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          multiline
          rows={3}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#7B2CBF',
                borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#7B2CBF',
            }
          }}
        />
      </Collapse>

      <Button onClick={() => setIsUploadSectionOpen(!isUploadSectionOpen)} sx={{
        width: '100%',
        justifyContent: 'space-between',
        textTransform: 'none',
        mb: 1,
        color: 'text.primary',
        '&:hover': { backgroundColor: 'rgba(123, 44, 191, 0.05)' }
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Add Photos & Videos</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formData.tempFiles && formData.tempFiles.length > 0 ? `${formData.tempFiles.length} selected` : 'Optional'}
        </Typography>
      </Button>
      <Collapse in={isUploadSectionOpen}>
        <Box component="label" sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed',
          borderColor: '#7B2CBF',
          borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
          borderRadius: 2,
          p: 3,
          height: 200,
          cursor: 'pointer',
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(123, 44, 191, 0.05)',
            '& .upload-icon': { transform: 'scale(1.1)', color: '#7B2CBF' },
            '& .upload-text': { color: '#7B2CBF' },
          },
        }}>
          <input type="file" hidden multiple accept="image/*,video/*" onChange={handleFileChange} />
          <AddPhotoAlternate className="upload-icon" sx={{ fontSize: 48, color: 'rgba(0, 0, 0, 0.2)', mb: 1, transition: 'all 0.3s ease' }} />
          <Typography className="upload-text" variant="h6" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.2)', transition: 'all 0.3s ease' }}>
            {formData.tempFiles.length > 0 ? 'Add more photos and videos' : 'Add photos and videos here'}
          </Typography>
          {isUploading && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 3,
                mt: 2,
                backgroundColor: 'rgba(123, 44, 191, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                  borderRadius: 3,
                }
              }}
            />
          )}
        </Box>
        
        {/* Display selected files with option to remove */}
        {formData.tempFiles.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Selected files ({formData.tempFiles.length}):
            </Typography>
            <Grid container spacing={1}>
              {formData.tempFiles.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      border: '1px solid rgba(123, 44, 191, 0.2)',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {file.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Remove file from tempFiles
                        setFormData(prev => ({
                          ...prev,
                          tempFiles: prev.tempFiles.filter((_, i) => i !== index)
                        }));
                      }}
                      sx={{ color: '#E31B88' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Collapse>
    </Box>
  );

  const renderStepOne = () => (
    <Box>
      <Typography variant="h6" sx={{
        mb: 1,
        fontWeight: 600,
        background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        Unlock Date
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          color: 'text.secondary',
        }}
      >
        Choose when you want your TimeCapsule to be unlocked
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateCalendar
          value={formData.unlockDate}
          onChange={handleDateChange}
          disablePast
          shouldDisableDate={(date) => {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            const normalizedDate = new Date(date);
            normalizedDate.setHours(0, 0, 0, 0);
            return normalizedDate.getTime() === currentDate.getTime();
          }}
          sx={{
            '& .MuiPickersCalendarHeader-root': { marginTop: '16px', marginBottom: '0px' },
            '& .MuiDayCalendar-weekDayLabel': {
              mr: '2px',
              width: '40px',
              height: '40px',
              background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600,
            },
            '& .MuiPickersDay-root': {
              width: '40px',
              height: '40px',
              transition: 'all 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(123, 44, 191, 0.1)' },
            },
            '& .MuiPickersDay-root.Mui-selected': {
              background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
              color: 'white',
              '&:hover': { background: 'linear-gradient(90deg, #6A24A6, #D41A7A)' },
            },
            '& .MuiPickersDay-today': { borderColor: '#7B2CBF' },
          }}
        />
      </LocalizationProvider>
      {formErrors.unlockDate && (
        <Typography color="error" variant="caption" sx={{ mt: 2 }}>{formErrors.unlockDate}</Typography>
      )}

      <Typography variant="h6" sx={{
        mt: 3,
        mb: 0,
        fontWeight: 600,
        background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        Sharing
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          color: 'text.secondary',
        }}
      >
        Share your TimeCapsule with friends and family
      </Typography>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Switch
            checked={formData.isShared}
            onChange={handleSharingToggle}
            name="isShared"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#7B2CBF' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7B2CBF' },
            }}
          />
        }
        label="Share with friends"
      />
      {formData.isShared && (
        <Autocomplete
          multiple
          options={friends}
          getOptionLabel={(friend) => friend.name}
          onChange={handleAccessChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#7B2CBF',
                borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#7B2CBF',
            },
            '& .MuiChip-root': {
              backgroundColor: 'rgba(123, 44, 191, 0.1)',
              '& .MuiChip-deleteIcon': {
                color: '#7B2CBF',
                '&:hover': {
                  color: '#E31B88',
                },
              },
            },
          }}
          renderOption={(props, option) => (
            <ListItem {...props}>
              <ListItemAvatar>
                <Avatar src={option.picture} alt={option.name} />
              </ListItemAvatar>
              <ListItemText
                primary={option.name}
                secondary={option.username}
                primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                secondaryTypographyProps={{ sx: { color: 'text.secondary' } }}
              />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Friends"
              placeholder="Search and select friends to share with"
              helperText="You can search by name or username"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#7B2CBF',
                    borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
                  },
                },
                '& .MuiChip-root': {
                  backgroundColor: 'rgba(123, 44, 191, 0.1)',
                  '& .MuiChip-deleteIcon': { color: '#7B2CBF', '&:hover': { color: '#E31B88' } },
                },
              }}
            />
          )}
        />
      )}
    </Box>
  );

  const renderStepTwo = () => (
    <Box>
      <Typography variant="h6" sx={{
        mb: 3,
        fontWeight: 600,
        background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        Choose Your Theme
      </Typography>
      {renderThemeCards()}
      {user?.subscriptionStatus !== "premium" && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            display: 'inline-block',
            '&:hover': { color: '#7B2CBF', cursor: 'pointer' },
          }}>
            Upgrade to premium to access all themes!
          </Typography>
        </Box>
      )}
    </Box>
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: return renderStepZero();
      case 1: return renderStepOne();
      case 2: return renderStepTwo();
      default: return 'Unknown step';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: isMobile ? '16px' : '32px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {showLandingDialog ? (
          renderLandingDialog()
        ) : (
          <>
            <DialogTitle sx={{ p: 0, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}>
                    Create Time Capsule
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                    {activeStep === 0 && "Let's start by adding your memories and details"}
                    {activeStep === 1 && "Set when to unlock and who to share with"}
                    {activeStep === 2 && "Choose a beautiful theme for your capsule"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCancel}
                size="small"
                sx={{ top: isMobile ? -34 : -22, color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, mb: 4 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  mb: 4,
                  mt: 0.5,
                  '& .MuiStepIcon-root': {
                    color: 'rgba(123, 44, 191, 0.2)',
                    transition: 'all 0.3s ease',
                    '&.Mui-active': { color: '#7B2CBF', transform: 'scale(1.1)' },
                    '&.Mui-completed': { color: '#7B2CBF' },
                  },
                  '& .MuiStepLabel-label': {
                    transition: 'all 0.3s ease',
                    '&.Mui-active': {
                      background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      transform: 'scale(1.05)',
                    },
                    '&.Mui-completed': { color: '#7B2CBF' },
                  },
                  '& .MuiStepConnector-line': { borderColor: 'rgba(123, 44, 191, 0.2)' },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ mt: 3 }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {getStepContent(activeStep)}
                  </motion.div>
                </AnimatePresence>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 0, gap: 2 }}>
              <Button
                onClick={activeStep === 0 ? handleBackToLanding : handleBack}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  flex: 1,
                  py: 1.5,
                  color: '#7B2CBF',
                  borderColor: '#7B2CBF',
                  '&:hover': { backgroundColor: 'rgba(123, 44, 191, 0.1)' },
                  '&:disabled': { color: 'rgba(0, 0, 0, 0.38)', borderColor: 'rgba(0, 0, 0, 0.12)' },
                }}
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    flex: 1,
                    py: 1.5,
                    backgroundColor: '#7B2CBF',
                    '&:hover': { backgroundColor: '#6A24A6' },
                  }}
                >
                  Create
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    flex: 1,
                    py: 1.5,
                    backgroundColor: '#7B2CBF',
                    '&:hover': { backgroundColor: '#6A24A6' },
                    '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.38)' },
                  }}
                >
                  Next
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {showQRScanner && <QRScanner onScan={handleQRScanComplete} onClose={handleQRScanCancel} />}
    </>
  );
};

export default WizardModal;
